/**
 * F-30 image drop & paste (re-amended 2026-07-14 — images are ALWAYS elements;
 * the inline image cell value is retired), widened 2026-09-08 (F-135) to ANY
 * file: a video, an audio clip, a PDF, a project file. Every dropped or
 * pasted file becomes an element:
 *
 *  - dragover highlights the CURRENT target: a cell outline when the pointer
 *    is over a grid cell (via GridApi.hitTestCell), or a drop-shadow ghost over
 *    empty canvas;
 *  - drop uploads — a png/jpeg/gif/webp via POST /api/boards/:id/assets (with
 *    the client-side re-encode), anything else via POST /api/boards/:id/files
 *    (the server sniffs the bytes and says what they are) — a small progress
 *    chip shows while in flight — then creates an IMAGE or FILE element:
 *      pointer over a cell  → a CONTAINED element anchored to that cell
 *                             (dx/dy = pointer offset within the cell, F-36;
 *                             the cell grows to fit it, F-26);
 *      pointer over canvas  → a board-anchored floating element at the drop
 *                             point, natural size capped to 480 board px;
 *  - paste follows the selection: a contained element in the selected cell,
 *    else a floating element at the viewport center.
 *
 * The one file this layer does NOT take is a script (.txt/.csv/.tsv): that is
 * the F-90 import's (ImportScriptLayer listens on the same container). Music
 * dropped on a section's BGM slot is the slot's (BgmSlotLayer stops the event
 * before it reaches this layer); dropped anywhere else it is an audio element
 * with a player, like any other file.
 *
 * Multiple elements may share a cell, so a cell-drop no longer overwrites cell
 * content and no longer needs the F-42 soft-lock block. All writes go through
 * session (shared accessors, LOCAL_ORIGIN → undoable).
 */
import {
  FILE_NAME_MAX_LENGTH,
  fitContainedImage,
  screenToBoard,
  type Anchor,
  type FileElementPayload,
  type FileMedia,
  type ImageElementPayload,
  type LinkCardElementPayload,
} from "@kola/shared";
import { useCallback, useEffect, useState, type RefObject } from "react";

import { unfurl, uploadAsset, uploadFileAsset } from "../../core/api.js";
import type { CanvasStore } from "../../core/canvas.js";
import type { BoardSession } from "../../core/session.js";
import { linkCardSizeForPayload } from "../../registries/element-types/linkCard/aspect.js";
import { showToast } from "../../ui/toast.js";
import type { GridApi } from "../board/GridTable.js";
import { containedDropAnchor } from "./contained.js";
import {
  cappedElementSize,
  dragMayCarryUpload,
  fileElementSize,
  uploadRouteFor,
} from "./file-kind.js";
import { probeVideoSize } from "./media-probe.js";
import { optimizeForUpload } from "./optimizeImage.js";
import { singleHttpUrl } from "./link-url.js";

// The F-30 sizing rule now lives with the other file-kind helpers; re-exported
// so existing importers keep their path.
export { cappedElementSize, DROPPED_IMAGE_MAX_BOARD_PX } from "./file-kind.js";

/** F-31: skeleton link-card size (board px) before the unfurl resolves. */
export const LINK_CARD_W = 280;
export const LINK_CARD_H = 96;

/**
 * F-31: resolve a pasted URL's link-card element (fire-and-forget). The
 * skeleton ALWAYS resolves to ready/error — a network failure (throw) falls
 * back to the domain-only error rung, never a permanent spinner.
 *
 * F-31(a), 2026-07-15: the server also returns the STORED thumbnail's intrinsic
 * size, which is persisted into the payload and drives the card's real aspect —
 * payload and geometry commit together via `session.applyUnfurl` (UNFURL_ORIGIN
 * — off undo; the resize is guarded on the card still being skeleton-sized).
 */
async function resolveUnfurl(
  session: BoardSession,
  elementId: string,
  href: string,
  domain: string,
): Promise<void> {
  let payload: LinkCardElementPayload;
  try {
    const result = await unfurl(session.boardId, href);
    payload = { kind: "linkCard", url: href, domain: result.domain, status: result.status };
    if (result.title !== undefined) payload.title = result.title;
    if (result.description !== undefined) payload.description = result.description;
    if (result.thumbnailAssetId !== undefined) payload.thumbnailAssetId = result.thumbnailAssetId;
    if (result.favicon !== undefined) payload.favicon = result.favicon;
    // F-31(a/b/c): the ratio + video fields. `kind:"video"` is the route's
    // wire name; the payload calls it `media` (an element payload already has
    // a `kind` — it is what discriminates linkCard from image).
    if (result.thumbnailW !== undefined) payload.thumbW = result.thumbnailW;
    if (result.thumbnailH !== undefined) payload.thumbH = result.thumbnailH;
    if (result.provider !== undefined) payload.provider = result.provider;
    if (result.kind === "video") payload.media = "video";
  } catch {
    // Network failure / non-2xx / aborted → error rung (still a clickable card).
    payload = { kind: "linkCard", url: href, domain, status: "error" };
  }
  session.applyUnfurl(elementId, payload, linkCardSizeForPayload(payload), {
    w: LINK_CARD_W,
    h: LINK_CARD_H,
  });
}

/**
 * What an upload became, ready to place: an image element's payload or a file
 * element's. Decided by the SERVER's reading of the bytes — a PNG that arrived
 * with a .dat extension is an image, whatever the picker thought.
 */
type Uploaded =
  | { kind: "image"; payload: Omit<ImageElementPayload, "kind"> }
  | { kind: "file"; payload: Omit<FileElementPayload, "kind"> };

/** The name recorded for a file: what the OS called it, clamped; blank when it had none. */
function uploadName(file: File): string | undefined {
  const name = file.name.replace(/\s+/g, " ").trim().slice(0, FILE_NAME_MAX_LENGTH);
  return name.length === 0 ? undefined : name;
}

/** Initial displayed size of an upload (board px), contained in a cell or floating. */
function sizeFor(uploaded: Uploaded, contained: boolean): { w: number; h: number } {
  if (uploaded.kind === "image") {
    const { w, h } = uploaded.payload;
    return contained ? fitContainedImage(w, h) : cappedElementSize(w, h);
  }
  return fileElementSize(uploaded.payload.media, uploaded.payload.w, uploaded.payload.h);
}

type DropTarget =
  | { kind: "cell"; rect: { left: number; top: number; width: number; height: number } }
  | { kind: "canvas"; x: number; y: number }; // container-relative pointer

export interface ImageDropLayerProps {
  session: BoardSession;
  canvas: CanvasStore;
  containerRef: RefObject<HTMLDivElement | null>;
  gridApi: RefObject<GridApi | null>;
}

export function ImageDropLayer({ session, canvas, containerRef, gridApi }: ImageDropLayerProps) {
  const [target, setTarget] = useState<DropTarget | null>(null);
  const [uploads, setUploads] = useState(0);

  /**
   * Upload one file by the route its type deserves (`uploadRouteFor`) and
   * describe what it became. A board image goes up re-encoded (F-30) and is
   * an image element; everything else goes up byte-for-byte and is whatever
   * the server sniffed — an image after all, or a video / audio / file. A
   * video whose header carried no picture size is measured by the browser
   * from the local file so its box has the right shape.
   */
  const uploadOne = useCallback(
    async (file: File): Promise<Uploaded | null> => {
      setUploads((n) => n + 1);
      try {
        const name = uploadName(file);
        if (uploadRouteFor(file) === "image") {
          const asset = await uploadAsset(session.boardId, await optimizeForUpload(file));
          return {
            kind: "image",
            payload: { assetId: asset.assetId, w: asset.w, h: asset.h, ...(name === undefined ? {} : { name }) },
          };
        }
        const result = await uploadFileAsset(session.boardId, file);
        if (result.media === "image" && result.w !== undefined && result.h !== undefined) {
          return {
            kind: "image",
            payload: { assetId: result.assetId, w: result.w, h: result.h, ...(name === undefined ? {} : { name }) },
          };
        }
        const media: FileMedia = result.media === "image" ? "file" : result.media;
        let size = result.w !== undefined && result.h !== undefined ? { w: result.w, h: result.h } : null;
        if (media === "video" && size === null) size = await probeVideoSize(file);
        return {
          kind: "file",
          payload: {
            assetId: result.assetId,
            name: name ?? `file.${media === "video" ? "mp4" : "bin"}`,
            contentType: result.contentType,
            size: result.size,
            media,
            ...(size === null ? {} : { w: size.w, h: size.h }),
          },
        };
      } catch (error) {
        showToast(error instanceof Error ? error.message : "Upload failed");
        return null;
      } finally {
        setUploads((n) => n - 1);
      }
    },
    [session],
  );

  /** Create the element for an upload: contained in a cell (anchor given) or at a board point. */
  const place = useCallback(
    (uploaded: Uploaded, x: number, y: number, anchor?: Anchor): string | null => {
      const size = sizeFor(uploaded, anchor !== undefined);
      return uploaded.kind === "image"
        ? session.createImageElement(x, y, size.w, size.h, uploaded.payload, anchor)
        : session.createFileElement(x, y, size.w, size.h, uploaded.payload, anchor);
    },
    [session],
  );

  /** Place an upload as a floating element centered on a board point. */
  const placeFloating = useCallback(
    (uploaded: Uploaded, boardX: number, boardY: number) => {
      const size = sizeFor(uploaded, false);
      place(uploaded, Math.round(boardX - size.w / 2), Math.round(boardY - size.h / 2));
    },
    [place],
  );

  /**
   * F-31: create a skeleton link-card from a pasted URL, then unfurl it. Follows
   * the selection like the image paste: a CONTAINED card in the selected (anchor)
   * cell if one is selected, else a board-anchored floating card at the viewport
   * center. The skeleton appears instantly; the async unfurl patches it.
   */
  const pasteLinkCard = useCallback(
    (url: URL) => {
      const state = session.store.getState();
      const sheetId = state.sheetId;
      const selection = state.localSelection;
      const { camera, viewport } = canvas.getState();
      const center = screenToBoard(camera, { x: viewport.width / 2, y: viewport.height / 2 });
      const href = url.toString();
      const domain = url.hostname;

      let anchor: Anchor | undefined;
      let x = Math.round(center.x - LINK_CARD_W / 2);
      let y = Math.round(center.y - LINK_CARD_H / 2);
      if (sheetId !== null && selection !== null) {
        const { rowId, colId } = selection.anchor;
        if (session.board.hasRow(sheetId, rowId) && session.board.hasColumn(sheetId, colId)) {
          anchor = containedDropAnchor(rowId, colId, 0, 0);
          x = Math.round(center.x);
          y = Math.round(center.y);
        }
      }

      const elementId = session.createLinkCardElement(
        x,
        y,
        LINK_CARD_W,
        LINK_CARD_H,
        { url: href, domain, status: "loading" },
        anchor,
      );
      if (elementId === null) return; // editing frozen (F-45)
      void resolveUnfurl(session, elementId, href, domain);
    },
    [session, canvas],
  );

  /* ---------------- drag & drop (native listeners) ------------------- */

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const hasFiles = (event: DragEvent): boolean =>
      event.dataTransfer !== null && [...event.dataTransfer.types].includes("Files");

    const computeTarget = (event: DragEvent): DropTarget => {
      const hit = gridApi.current?.hitTestCell(event.clientX, event.clientY) ?? null;
      const rect = container.getBoundingClientRect();
      if (hit !== null) {
        return {
          kind: "cell",
          rect: {
            left: hit.rect.left - rect.left,
            top: hit.rect.top - rect.top,
            width: hit.rect.width,
            height: hit.rect.height,
          },
        };
      }
      return { kind: "canvas", x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    // F-90 coexistence: a .txt/.csv/.tsv drag belongs to ImportScriptLayer, and
    // promising it a cell ("your file lands here") would be a lie. File NAMES
    // are unavailable mid-drag, so the guess is by MIME type (file-kind.ts
    // `dragMayCarryUpload`) — the drop below decides by name, so a guess that
    // was wrong costs the ghost, never the behavior.
    const onDragOver = (event: DragEvent) => {
      if (!hasFiles(event)) return;
      // ALWAYS preventDefault for file drags — even while frozen (F-45).
      // Otherwise the browser's default file-drop action navigates the tab
      // away from the app when the user drops anyway.
      event.preventDefault();
      if (!dragMayCarryUpload(event.dataTransfer?.items ?? [])) {
        setTarget(null);
        return;
      }
      if (session.store.getState().frozen) {
        if (event.dataTransfer) event.dataTransfer.dropEffect = "none";
        setTarget(null);
        return;
      }
      if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
      setTarget(computeTarget(event));
    };

    const onDragLeave = (event: DragEvent) => {
      // Only clear when actually leaving the canvas (not moving between children).
      const related = event.relatedTarget as Node | null;
      if (related === null || !container.contains(related)) setTarget(null);
    };

    const onDrop = (event: DragEvent) => {
      setTarget(null);
      if (!hasFiles(event)) return;
      // preventDefault BEFORE the frozen check — the browser must never
      // navigate to the dropped file, read-only or not (F-45).
      event.preventDefault();
      // Every file but a script (the F-90 import's — ImportScriptLayer takes
      // those from the same drop event).
      const files = [...(event.dataTransfer?.files ?? [])].filter(
        (f) => uploadRouteFor(f) !== "script",
      );
      if (files.length === 0) return;
      if (session.store.getState().frozen) {
        showToast("The board is read-only while offline — nothing was added");
        return;
      }

      // Resolve targets NOW (docs/07 C4 spirit: the async upload must not
      // re-resolve against moved state).
      const hit = gridApi.current?.hitTestCell(event.clientX, event.clientY) ?? null;
      const rect = container.getBoundingClientRect();
      const camera = canvas.getState().camera;
      const dropBoard = screenToBoard(camera, {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
      // Pointer offset within the hit cell, in board units (hit.rect is client
      // px; the cell spans hit.rect.width/zoom board px) — the F-36 dx/dy.
      const inCellX = hit !== null ? (event.clientX - hit.rect.left) / camera.zoom : 0;
      const inCellY = hit !== null ? (event.clientY - hit.rect.top) / camera.zoom : 0;
      const sheetId = session.store.getState().sheetId;

      files.forEach((file, i) => {
        void uploadOne(file).then((uploaded) => {
          if (uploaded === null) return;
          if (i === 0 && hit !== null && sheetId !== null) {
            // Pointer over a grid cell → a CONTAINED element anchored to it
            // (F-30/F-36). Multiple elements may share a cell, so this never
            // overwrites cell content — no F-42 soft-lock block needed.
            if (session.board.hasRow(sheetId, hit.rowId) && session.board.hasColumn(sheetId, hit.colId)) {
              place(
                uploaded,
                Math.round(dropBoard.x),
                Math.round(dropBoard.y),
                containedDropAnchor(hit.rowId, hit.colId, inCellX, inCellY),
              );
              return;
            }
            showToast("That cell was deleted while uploading — added the file to the canvas");
          }
          // Empty canvas (or overflow files) → floating element at the drop
          // point; extra files cascade so they don't stack invisibly.
          const offset = i * 24;
          placeFloating(uploaded, dropBoard.x + offset, dropBoard.y + offset);
        });
      });
    };

    container.addEventListener("dragover", onDragOver);
    container.addEventListener("dragleave", onDragLeave);
    container.addEventListener("drop", onDrop);
    return () => {
      container.removeEventListener("dragover", onDragOver);
      container.removeEventListener("dragleave", onDragLeave);
      container.removeEventListener("drop", onDrop);
    };
  }, [session, canvas, containerRef, gridApi, uploadOne, place, placeFloating]);

  /* ---------------- paste (F-30/F-16) -------------------------------- */

  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      if (session.store.getState().frozen) return;
      const targetEl = event.target as HTMLElement | null;
      if (
        targetEl &&
        (targetEl.tagName === "INPUT" ||
          targetEl.tagName === "TEXTAREA" ||
          targetEl.isContentEditable)
      ) {
        return; // typing surfaces keep their native paste (incl. overlay editor)
      }
      const clipboard = event.clipboardData;
      if (!clipboard) return;
      const text = clipboard.getData("text/plain");
      // F-31: a SINGLE http(s) URL becomes a link-card element (before the
      // image/TSV branches). Multi-line / mailto: / non-URL text returns null
      // and falls through to the grid's TSV paste unchanged. Capture-phase +
      // stopPropagation keeps the grid's TSV handler from also processing it.
      const url = singleHttpUrl(text);
      if (url !== null) {
        event.preventDefault();
        event.stopPropagation();
        pasteLinkCard(url);
        return;
      }
      // F-16: text wins over an image RENDITION. Excel (and friends) put a
      // bitmap snapshot of copied cells on the clipboard ALONGSIDE the
      // text/plain TSV — treating "has an image item" as an image paste
      // would upload a screenshot of the cells and silently drop the actual
      // values. Any non-empty text (even whitespace-only TSV of empty
      // cells) means this is a cell paste and belongs to the grid's TSV
      // paste handler. Only text-free clipboards (true screenshots, copied
      // image files) are file pastes.
      if (text.length > 0) return;
      const items = clipboard.items;
      const files: File[] = [];
      for (const item of items) {
        // Any file the clipboard carries (F-135) — a screenshot, a copied
        // image, and whatever else the OS exposes as a file item.
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file && uploadRouteFor(file) !== "script") files.push(file);
        }
      }
      if (files.length === 0) return; // TSV/text paste belongs to the grid (F-16)
      // Capture-phase + stopPropagation: the grid's TSV paste handler must
      // not also process this.
      event.preventDefault();
      event.stopPropagation();

      const state = session.store.getState();
      const sheetId = state.sheetId;
      const selection = state.localSelection;
      const { camera, viewport } = canvas.getState();
      const center = screenToBoard(camera, { x: viewport.width / 2, y: viewport.height / 2 });

      files.forEach((file, i) => {
        void uploadOne(file).then((uploaded) => {
          if (uploaded === null) return;
          if (i === 0 && sheetId !== null && selection !== null) {
            // Paste follows the selection → a CONTAINED element in the selected
            // (anchor) cell, inset at the F-36 default offset (F-30).
            const { rowId, colId } = selection.anchor;
            if (session.board.hasRow(sheetId, rowId) && session.board.hasColumn(sheetId, colId)) {
              place(
                uploaded,
                Math.round(center.x),
                Math.round(center.y),
                containedDropAnchor(rowId, colId, 0, 0),
              );
              return;
            }
          }
          placeFloating(uploaded, center.x + i * 24, center.y + i * 24);
        });
      });
    };
    window.addEventListener("paste", onPaste, true);
    return () => window.removeEventListener("paste", onPaste, true);
  }, [session, canvas, uploadOne, place, placeFloating, pasteLinkCard]);

  /* ---------------- overlay UI --------------------------------------- */

  return (
    <>
      {target?.kind === "cell" && (
        <div
          className="drop-highlight-cell"
          style={{
            left: target.rect.left,
            top: target.rect.top,
            width: target.rect.width,
            height: target.rect.height,
          }}
        />
      )}
      {target?.kind === "canvas" && (
        <div className="drop-ghost" style={{ left: target.x, top: target.y }} />
      )}
      {uploads > 0 && (
        <div className="upload-chip" role="status">
          <span className="upload-chip-spinner" />
          Uploading {uploads === 1 ? "file" : `${uploads} files`}…
        </div>
      )}
    </>
  );
}
